import axios from "axios";
import { useOktaConfig } from "./CredentialsContext";
import { createContext, useEffect, useState, useCallback, useRef } from "react";

export const AdminPanelContext = createContext([]);

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function AdminPanelProvider({ children }) {
    const { loginToken } = useOktaConfig();

    const [adminPanelData, setAdminPanelData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Use ref to track if initial fetch has been done
    const hasFetchedRef = useRef(false);

    // Stable auth error handler - doesn't depend on navigate
    const handleAuthError = useCallback(() => {
        localStorage.removeItem("Firebase Auth");
        localStorage.removeItem("okta-token-storage");
        localStorage.removeItem("okta-cache-storage");
        // Use window.location instead of navigate to avoid dependency
        window.location.href = '/';
    }, []);

    // Stable API call function
    const getMarketingUsers = useCallback(async () => {
        if (!loginToken) return;

        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(`${API_BASE_URL}/marketing/users`, {
                headers: {
                    Authorization: `Bearer ${loginToken}`,
                },
            });

            if (response.status === 200) {
                const { message, ...data } = response.data;
                setAdminPanelData(data);
            }
        } catch (error) {
            if (error?.response?.status === 401) {
                handleAuthError();
            }
        } finally {
            setLoading(false);
        }
    }, [loginToken, handleAuthError]);

    const addMarketingUsers = useCallback(async (payload) => {
        if (!loginToken) return { success: false, error: "No auth token" };

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/marketing/users`, payload, {
                headers: {
                    Authorization: `Bearer ${loginToken}`,
                },
            });

            if (response.status === 200 || response.status === 201) {
                const { message, userInfo, ...updatedData } = response.data;
                setAdminPanelData(updatedData);
                return { success: true, data: response.data };
            }
        } catch (error) {
            console.log("❌ Error adding user:", error);
            setError(error.response?.data?.message || error.message);

            if (error?.response?.status === 401) {
                handleAuthError();
            }
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [loginToken, handleAuthError]);

    const updateMarketingUsers = useCallback(async (payload) => {
        const { id, ...newPayload } = payload;
        if (!loginToken) return { success: false, error: "No auth token" };

        setLoading(true);
        setError(null);

        try {
            const response = await axios.put(`${API_BASE_URL}/marketing/users/${id}`, newPayload, {
                headers: {
                    Authorization: `Bearer ${loginToken}`,
                },
            });

            if (response.status === 200 || response.status === 201) {
                const { message, user, ...updatedData } = response.data;
                setAdminPanelData(updatedData);

                return { success: true, data: response.data };
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message);

            if (error?.response?.status === 401) {
                handleAuthError();
            }
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [loginToken, handleAuthError]);

    const deleteMarketingUsers = useCallback(async (id) => {
        if (!loginToken) return { success: false, error: "No auth token" };

        setLoading(true);
        setError(null);

        try {
            const response = await axios.delete(`${API_BASE_URL}/marketing/users/${id}`, {
                headers: {
                    Authorization: `Bearer ${loginToken}`,
                },
            });

            if (response.status === 200 || response.status === 204) {
                const { message, deleted, ...updatedData } = response.data;
                setAdminPanelData(updatedData);
                return { success: true };
            }
        } catch (error) {
            console.error("❌ Error deleting user:", error);
            setError("Failed to delete user");

            if (error?.response?.status === 401) {
                handleAuthError();
            }
            return { success: false, error: error.message };
        } finally {
            setLoading(false);
        }
    }, [loginToken, handleAuthError]);

    // Fetch data only once when loginToken becomes available
    useEffect(() => {
        if (loginToken && !hasFetchedRef.current) {
            hasFetchedRef.current = true;
            getMarketingUsers();
        }
    }, [loginToken, getMarketingUsers]);

    // Reset fetch flag when token changes (login/logout)
    useEffect(() => {
        if (!loginToken) {
            hasFetchedRef.current = false;
        }
    }, [loginToken]);

    const contextValue = {
        adminPanelData,
        setAdminPanelData,
        addMarketingUsers,
        updateMarketingUsers,
        deleteMarketingUsers,
        loading,
        error,
        setError,
        refetch: getMarketingUsers
    };

    return (
        <AdminPanelContext.Provider value={contextValue}>
            {children}
        </AdminPanelContext.Provider>
    );
}
